import {
  roleLabels,
  roles,
  type AuthUser,
  type Role,
  type UserSummary,
} from "@apple-bee/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy,
  KeyRound,
  Link2,
  LockKeyhole,
  Plus,
  ShieldCheck,
  UserCheck,
  UserX,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { EmptyState, LoadingBlock, Message } from "../components/Feedback";
import { Modal } from "../components/Modal";
import { dateTimeFormatter } from "../format";

type Invitation = {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  roleLabel: string;
  expiresAt: string;
  createdAt: string;
};

function readInvitationPrompt(): boolean {
  const url = new URL(window.location.href);
  const shouldOpen = url.searchParams.get("new-invite") === "1";
  if (shouldOpen) {
    url.searchParams.delete("new-invite");
    window.history.replaceState({}, "", url);
  }
  return shouldOpen;
}

export function AdminPage({ currentUser }: { currentUser: AuthUser }) {
  const queryClient = useQueryClient();
  const canManageUsers = currentUser.roles.includes("IT_ADMIN");
  const allowedInvitationRoles: Role[] = canManageUsers
    ? [...roles]
    : roles.filter(
        (role) => !["CEO", "IT_ADMIN"].includes(role),
      );
  const [inviteOpen, setInviteOpen] = useState(readInvitationPrompt);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [recoveryLink, setRecoveryLink] = useState<string | null>(null);
  const [recoveryUser, setRecoveryUser] = useState<UserSummary | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api<{ users: UserSummary[] }>("/api/admin/users"),
    enabled: canManageUsers,
  });
  const invitationsQuery = useQuery({
    queryKey: ["admin-invitations"],
    queryFn: () =>
      api<{ invitations: Invitation[] }>("/api/admin/invitations"),
    enabled: canManageUsers,
  });

  async function refresh() {
    if (!canManageUsers) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-invitations"] }),
    ]);
  }

  async function userAction(
    user: UserSummary,
    action: "toggle-active" | "reset-mfa",
  ) {
    const prompt =
      action === "reset-mfa"
        ? `Сбросить двухэтапную проверку для ${user.displayName}? Все его сессии будут закрыты.`
        : `${user.isActive ? "Отключить" : "Включить"} аккаунт ${user.displayName}?`;
    if (!window.confirm(prompt)) return;
    setActionError(null);
    try {
      await api(`/api/admin/users/${user.id}/${action}`, { method: "POST" });
      await refresh();
    } catch (caught) {
      setActionError(errorMessage(caught));
    }
  }

  async function revokeInvitation(invitation: Invitation) {
    if (!window.confirm(`Отозвать приглашение для ${invitation.email}?`)) return;
    try {
      await api(`/api/admin/invitations/${invitation.id}/revoke`, {
        method: "POST",
      });
      await refresh();
    } catch (caught) {
      setActionError(errorMessage(caught));
    }
  }

  async function startPasswordRecovery(user: UserSummary) {
    if (
      !window.confirm(
        `Создать ссылку восстановления для ${user.displayName}? Сначала подтвердите личность пользователя по доверенному каналу. Все его активные сессии будут закрыты.`,
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      const result = await api<{
        recovery: { recoveryUrl: string };
      }>(`/api/admin/users/${user.id}/password-reset`, { method: "POST" });
      setRecoveryUser(user);
      setRecoveryLink(result.recovery.recoveryUrl);
    } catch (caught) {
      setActionError(errorMessage(caught));
    }
  }

  if (canManageUsers && usersQuery.isLoading) {
    return <LoadingBlock label="Загружаем учётные записи…" />;
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Управление доступом</p>
          <h2>{canManageUsers ? "Пользователи и приглашения" : "Пригласить сотрудника"}</h2>
          <p>
            {canManageUsers
              ? "Открытая регистрация отключена. Новые аккаунты создаются только по одноразовой ссылке."
              : "Генеральный директор может создать одноразовое приглашение для рабочей роли. Управление аккаунтами остаётся у IT-администратора."}
          </p>
        </div>
        <button
          className="button button--primary"
          type="button"
          onClick={() => {
            setInviteLink(null);
            setInviteOpen(true);
          }}
        >
          <Plus size={18} />
          Пригласить пользователя
        </button>
      </section>

      {canManageUsers ? (
        <>
      <section className="admin-summary">
        <div>
          <UserCheck size={21} />
          <span>
            <strong>
              {(usersQuery.data?.users ?? []).filter((user) => user.isActive)
                .length}
            </strong>
            активных аккаунтов
          </span>
        </div>
        <div>
          <ShieldCheck size={21} />
          <span>
            <strong>
              {(usersQuery.data?.users ?? []).filter((user) => user.mfaEnabled)
                .length}
            </strong>
            с настроенной 2FA
          </span>
        </div>
        <div>
          <Link2 size={21} />
          <span>
            <strong>{invitationsQuery.data?.invitations.length ?? 0}</strong>
            активных приглашений
          </span>
        </div>
      </section>

      {actionError ? <Message tone="error">{actionError}</Message> : null}

      <section className="panel">
        <header className="panel__header">
          <div>
            <p className="eyebrow">Учётные записи</p>
            <h2>Пользователи системы</h2>
          </div>
        </header>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Роль</th>
                <th>2FA</th>
                <th>Последний вход</th>
                <th>Статус</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {(usersQuery.data?.users ?? []).map((user) => (
                <tr key={user.id} className={!user.isActive ? "row-disabled" : ""}>
                  <td>
                    <strong>{user.displayName}</strong>
                    <span className="table-subline">{user.email}</span>
                  </td>
                  <td>{user.roles.map((role) => roleLabels[role]).join(", ")}</td>
                  <td>
                    <span
                      className={`status-dot ${
                        user.mfaEnabled ? "status-dot--ok" : "status-dot--warn"
                      }`}
                    >
                      {user.mfaEnabled ? "Настроена" : "Не настроена"}
                    </span>
                  </td>
                  <td>
                    {user.lastLoginAt
                      ? dateTimeFormatter.format(new Date(user.lastLoginAt))
                      : "Ещё не входил(а)"}
                  </td>
                  <td>
                    <span
                      className={`status-pill ${
                        user.isActive
                          ? "status-pill--approved"
                          : "status-pill--cancelled"
                      }`}
                    >
                      {user.isActive ? "Активен" : "Отключён"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="icon-button"
                        type="button"
                        title="Восстановить пароль"
                        aria-label={`Восстановить пароль для ${user.displayName}`}
                        onClick={() => void startPasswordRecovery(user)}
                      >
                        <LockKeyhole size={17} />
                      </button>
                      <button
                        className="icon-button"
                        type="button"
                        title="Сбросить 2FA"
                        aria-label={`Сбросить 2FA для ${user.displayName}`}
                        onClick={() => void userAction(user, "reset-mfa")}
                      >
                        <KeyRound size={17} />
                      </button>
                      <button
                        className="icon-button"
                        type="button"
                        title={user.isActive ? "Отключить" : "Включить"}
                        aria-label={`${user.isActive ? "Отключить" : "Включить"} ${user.displayName}`}
                        onClick={() => void userAction(user, "toggle-active")}
                        disabled={user.id === currentUser.id}
                      >
                        {user.isActive ? (
                          <UserX size={17} />
                        ) : (
                          <UserCheck size={17} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <header className="panel__header">
          <div>
            <p className="eyebrow">Одноразовые ссылки</p>
            <h2>Ожидают активации</h2>
          </div>
        </header>
        {invitationsQuery.data?.invitations.length ? (
          <div className="invitation-list">
            {invitationsQuery.data.invitations.map((invitation) => (
              <div className="invitation-row" key={invitation.id}>
                <span className="avatar avatar--small">
                  {invitation.displayName.slice(0, 2).toUpperCase()}
                </span>
                <div>
                  <strong>{invitation.displayName}</strong>
                  <span>
                    {invitation.email} · {invitation.roleLabel}
                  </span>
                </div>
                <span>
                  До {dateTimeFormatter.format(new Date(invitation.expiresAt))}
                </span>
                <button
                  className="button button--danger-ghost button--small"
                  type="button"
                  onClick={() => void revokeInvitation(invitation)}
                >
                  Отозвать
                </button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Нет активных приглашений"
            description="Новая ссылка появится здесь и будет действовать 48 часов."
          />
        )}
      </section>
        </>
      ) : (
        <Message tone="info">
          Доступно только создание приглашений для операционного менеджера,
          бухгалтера, логистики, кладовщика или пользователя с просмотром. Роли
          CEO и IT-администратора здесь недоступны.
        </Message>
      )}

      {inviteOpen ? (
        <InvitationModal
          allowedRoles={allowedInvitationRoles}
          limitedRoles={!canManageUsers}
          inviteLink={inviteLink}
          onInviteLink={setInviteLink}
          onClose={() => setInviteOpen(false)}
          onCreated={refresh}
        />
      ) : null}
      {recoveryLink && recoveryUser ? (
        <AccountRecoveryModal
          user={recoveryUser}
          recoveryLink={recoveryLink}
          onClose={() => {
            setRecoveryLink(null);
            setRecoveryUser(null);
          }}
        />
      ) : null}
    </div>
  );
}

function AccountRecoveryModal({
  user,
  recoveryLink,
  onClose,
}: {
  user: UserSummary;
  recoveryLink: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      title="Ссылка восстановления создана"
      subtitle={`Для ${user.displayName}. Передайте ссылку только после проверки личности.`}
      onClose={onClose}
    >
      <div className="modal__body">
        <Message tone="success">
          Ссылка действует 30 минут и используется один раз. Все прежние сессии
          пользователя уже закрыты.
        </Message>
        <label className="copy-field">
          Ссылка восстановления
          <span>
            <input value={recoveryLink} readOnly />
            <button
              className="button button--secondary"
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(recoveryLink);
                setCopied(true);
              }}
            >
              <Copy size={17} /> {copied ? "Скопировано" : "Копировать"}
            </button>
          </span>
        </label>
        <Message tone="info">
          Не отправляйте ссылку в общий чат и не включайте её в журнал или
          скриншот. Если пользователь также потерял 2FA, отдельно выполните
          сброс 2FA.
        </Message>
        <div className="modal__actions">
          <button className="button button--primary" type="button" onClick={onClose}>
            Готово
          </button>
        </div>
      </div>
    </Modal>
  );
}

function InvitationModal({
  allowedRoles,
  limitedRoles,
  inviteLink,
  onInviteLink,
  onClose,
  onCreated,
}: {
  allowedRoles: Role[];
  limitedRoles: boolean;
  inviteLink: string | null;
  onInviteLink: (value: string) => void;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(() =>
    allowedRoles.includes("VIEWER") ? "VIEWER" : (allowedRoles[0] ?? "VIEWER"),
  );
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api<{
        invitation: { inviteUrl: string };
      }>("/api/admin/invitations", {
        method: "POST",
        body: JSON.stringify({ displayName, email, role }),
      });
      onInviteLink(result.invitation.inviteUrl);
      await onCreated();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={inviteLink ? "Приглашение создано" : "Новый пользователь"}
      subtitle={
        inviteLink
          ? "Передайте ссылку человеку через доверенный канал."
          : limitedRoles
            ? "CEO может приглашать только сотрудников с рабочими ролями."
            : "Выберите только ту роль, которая нужна для работы."
      }
      onClose={onClose}
    >
      {inviteLink ? (
        <div className="modal__body">
          <Message tone="success">
            Ссылка действует 48 часов и может быть использована только один раз.
          </Message>
          <label className="copy-field">
            Ссылка приглашения
            <span>
              <input value={inviteLink} readOnly />
              <button
                className="button button--secondary"
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(inviteLink);
                  setCopied(true);
                }}
              >
                <Copy size={17} /> {copied ? "Скопировано" : "Копировать"}
              </button>
            </span>
          </label>
          <div className="modal__actions">
            <button className="button button--primary" type="button" onClick={onClose}>
              Готово
            </button>
          </div>
        </div>
      ) : (
        <form className="modal__body form-grid" onSubmit={(event) => void submit(event)}>
          <label className="span-2">
            Имя и фамилия
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Как будет отображаться в системе"
              required
            />
          </label>
          <label className="span-2">
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.kz"
              required
            />
          </label>
          <label className="span-2">
            Роль
            <select value={role} onChange={(event) => setRole(event.target.value as Role)}>
              {allowedRoles.map((item) => (
                <option key={item} value={item}>
                  {roleLabels[item]}
                </option>
              ))}
            </select>
          </label>
          {error ? (
            <div className="span-2">
              <Message tone="error">{error}</Message>
            </div>
          ) : null}
          <div className="modal__actions span-2">
            <button className="button button--secondary" type="button" onClick={onClose}>
              Отмена
            </button>
            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? "Создаём…" : "Создать приглашение"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
